import { type TooltipPlacement } from "antd/lib/tooltip";
import { throttle } from "lodash-es";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { cn } from "@btnet/utilities-core";

import { useResizeObserver } from "utilities/resizeObserver/useResizeObserver";
import { useHasValueChanged } from "utilities/useHasValueChanged";

import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";

import "./TextTruncateMultiline.less";

export interface ITextTruncateMultilineProps {
    /**
     * Number of lines to show before truncating
     * @default 2
     */
    lines?: number;
    showPopover?: boolean;
    popoverPlacement?: TooltipPlacement;
    className?: string;
    style?: React.CSSProperties;
    popoverContent?: React.ReactNode;
    getPopoverContainer?: (triggerNode: HTMLElement) => HTMLElement;
    "data-testid"?: string;
    children: React.ReactNode;
}

export function TextTruncateMultiline(props: ITextTruncateMultilineProps) {
    const { lines = 2, showPopover = true, "data-testid": dataTestId } = props;

    const [isPopoverVisible, setIsPopoverVisible] = useState(false);
    const isTruncatedRef = useRef(false);

    const classes = cn("TextTruncateMultiline", props.className);

    const getIsTruncated = useCallback(
        (entry: HTMLElement | null) => {
            throttle((entry: HTMLElement | null) => {
                if (entry && showPopover) {
                    // For line-clamp, scrollHeight detection doesn't work reliably
                    // Instead, temporarily remove line-clamp and compare heights
                    const originalWebkitLineClamp = entry.style.webkitLineClamp;
                    const originalDisplay = entry.style.display;

                    // Get clamped height
                    const clampedHeight = entry.clientHeight;

                    // Temporarily remove line-clamp to get full height
                    entry.style.webkitLineClamp = "";
                    entry.style.display = "block";

                    const fullHeight = entry.clientHeight;

                    // Restore line-clamp
                    entry.style.webkitLineClamp = originalWebkitLineClamp;
                    entry.style.display = originalDisplay;

                    const isTruncated = fullHeight > clampedHeight;
                    isTruncatedRef.current = isTruncated;
                }
            }, 200)(entry);
        },
        [showPopover],
    );

    const getIsTruncatedResizeObserver = useCallback(
        (entry: ResizeObserverEntry | undefined) => {
            if (entry?.target && showPopover) {
                getIsTruncated(entry.target as HTMLElement);
            }
        },
        [getIsTruncated, showPopover],
    );

    const [divRef, onDivRefChange] = useResizeObserver<HTMLDivElement>(
        getIsTruncatedResizeObserver,
    );

    const [isContentChanged] = useHasValueChanged(props.children);

    // Wait for divNode to update layout properties like scrollHeight & clientHeight
    useLayoutEffect(() => {
        // skip if alwaysShowPopover
        if (isContentChanged) {
            getIsTruncated(divRef.current);
        }
    }, [isContentChanged, divRef, getIsTruncated]);

    const isPopoverEnabled = showPopover;

    useEffect(() => {
        if (isPopoverVisible && !isPopoverEnabled) {
            setIsPopoverVisible(false);
        }
    }, [isPopoverEnabled, isPopoverVisible]);

    const handlePopoverIsVisibleChange = useCallback(
        (visible) => {
            if ((isPopoverEnabled && !visible) || (isPopoverEnabled && isTruncatedRef.current)) {
                setIsPopoverVisible(visible);
            }
        },
        [isPopoverEnabled],
    );

    const containerStyle: React.CSSProperties = {
        WebkitLineClamp: lines,
        ...props.style,
    };

    return (
        <BTPopover
            content={props.popoverContent ?? props.children}
            placement={props.popoverPlacement ?? "topLeft"}
            mouseEnterDelay={0.3}
            visible={isPopoverVisible}
            onVisibleChange={handlePopoverIsVisibleChange}
            getPopupContainer={props.getPopoverContainer}
        >
            <div
                ref={onDivRefChange}
                className={classes}
                style={containerStyle}
                data-testid={dataTestId}
            >
                {props.children}
            </div>
        </BTPopover>
    );
}

export default TextTruncateMultiline;
